import React from "react";
import {observer} from "mobx-react-lite";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {useStores} from "../../../../stores";
import {GoldenRecordClusterType} from "../../../../services/ApiTypes.ts";
import {plural} from "../../../../utils/js-utils.ts";
import MaterialIcon from "../../../../components/icons/svg/MaterialIcon.svg?react";
import {SidebarLoading} from "../SidebarLoading.tsx";
import {CustomSkeleton} from "../../../../components/CustomSkeleton/CustomSkeleton";
import {CurrencyComponent} from "../../../../components/currency-component/CurrencyComponent.tsx";
import {Stack} from "@mui/system";
import ExpandStopIcon from "../../../../icons/svg/ExpandStopIcon.svg?react";
import {getGoldenRecordClusterToggleLeftPx} from "./goldenRecordLayout.constants.ts";

export type GoldenRecordClusterMenuProps = {
    collapsed: boolean;
    onToggleCollapsed: () => void;
};

const TOGGLE_BUTTON_SIZE_PX = 32;

export const GoldenRecordClusterMenu: React.FC<GoldenRecordClusterMenuProps> =
    observer(({collapsed, onToggleCollapsed}) => {
        const {authStore, bagStore, goldenRecordStore, rootAppStore} =
            useStores();
        const filterController =
            goldenRecordStore.grPageController.filterController;

        const toggleLeft = getGoldenRecordClusterToggleLeftPx(
            rootAppStore.isDrawerOpen,
            collapsed,
            TOGGLE_BUTTON_SIZE_PX,
        );

        return (
            <Box
                className={
                    "goldern-record-cluster-box" +
                    (collapsed ? " goldern-record-cluster-box--collapsed" : "")
                }
            >
                <Tooltip title={collapsed ? "Expand" : "Collapse"}>
                    <IconButton
                        size="small"
                        onClick={onToggleCollapsed}
                        aria-label={collapsed ? "Expand menu" : "Collapse menu"}
                        sx={{
                            mt: 0.5,
                            position: "fixed",
                            top: 54 + 64,
                            left: toggleLeft,
                            backgroundColor: "white !important",
                            border: "1px solid #E0E0E0",
                            //   borderRadius: "50%",
                            padding: 0.5,
                            width: TOGGLE_BUTTON_SIZE_PX,
                            height: TOGGLE_BUTTON_SIZE_PX,
                            zIndex: 1250,
                            transition: "all 0.3s",
                        }}
                    >
                        <ExpandStopIcon
                            style={{
                                transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                        />
                    </IconButton>
                </Tooltip>
                {!collapsed && (
                    <>
                        {filterController.isFetchingForTheFirstTime ? (
                            <SidebarLoading/>
                        ) : (
                            <>
                                <Box className="goldern-record-cluster-type-button-group">
                                    {[
                                        GoldenRecordClusterType.Material,
                                        GoldenRecordClusterType.Service,
                                    ].map((ct) => (
                                        <Button
                                            size={"small"}
                                            key={ct}
                                            disabled={filterController.clusters_request_busy}
                                            className={
                                                ct === filterController.cluster_type ? "selected" : ""
                                            }
                                            color="inherit"
                                            onClick={() => filterController.setClusterType(ct)}
                                        >
                                            {GoldenRecordClusterType[ct]}
                                        </Button>
                                    ))}
                                </Box>

                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search groups..."
                                    className="goldern-record-cluster-type-search"
                                    value={filterController.cluster_search}
                                    onChange={(e) =>
                                        filterController.setClusterSearch(e.target.value)
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                    }}
                                    slotProps={{
                                        input: {
                                            endAdornment: filterController.cluster_search && (
                                                <Close
                                                    sx={{cursor: "pointer"}}
                                                    onClick={() => filterController.setClusterSearch("")}
                                                />
                                            ),
                                        },
                                    }}
                                />
                                <List
                                    sx={{
                                        pt: 0,
                                        "& .Mui-selected": {
                                            borderRadius: "4px",
                                        },
                                        "& .MuiButtonBase-root:hover": {
                                            borderRadius: "4px",
                                        },
                                    }}
                                    dense
                                    className="menuList goldern-record-cluster-list"
                                    onScroll={(e: React.UIEvent<HTMLUListElement, UIEvent>) => {
                                        const target = e.target as HTMLUListElement;
                                        const distanceToBottom =
                                            target.scrollHeight -
                                            target.scrollTop -
                                            target.clientHeight;
                                        if (
                                            distanceToBottom < 30 &&
                                            !filterController.clusters_request_busy
                                        ) {
                                            filterController.setClustersPage(
                                                filterController.clusters_page + 1,
                                            );
                                        }
                                    }}
                                    subheader={
                                        <ListSubheader className="goldern-record-cluster-list-subheader">
                                            {GoldenRecordClusterType[filterController.cluster_type]}
                                            &nbsp;Groups (
                                            {filterController.clusters_request_busy
                                                ? "Loading"
                                                : (filterController.clusters_count_server ?? "")}
                                            )
                                        </ListSubheader>
                                    }
                                >
                                    {filterController.clusters.map((cluster) => (
                                        <ListItemButton
                                            key={cluster.id}
                                            className="p-2"
                                            selected={
                                                cluster.id === filterController.selected_cluster_id
                                            }
                                            onClick={() =>
                                                filterController.setSelectedClusterInfo(
                                                    cluster.id,
                                                    cluster.harmonized_name,
                                                )
                                            }
                                            sx={{
                                                py: "4px !important",
                                            }}
                                        >
                                            <ListItemIcon className="goldern-record-cluster-li-icon">
                                                <MaterialIcon/>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={cluster.harmonized_name}
                                                sx={{
                                                    "& .MuiListItemText-primary": {
                                                        color: "#1A1B20",
                                                    },
                                                    "& .MuiTouchRipple-root": {
                                                        margin: "4px 0",
                                                    },
                                                }}
                                                secondary={
                                                    <>
                                                        <Stack direction="row" gap="4px">
                                                            <Typography
                                                                fontSize={10}
                                                                fontWeight={500}
                                                                color="#44474E"
                                                                noWrap
                                                                flexShrink={0}
                                                            >
                                                                <CurrencyComponent
                                                                    v={cluster.spend_sum}
                                                                    nDigits={3}
                                                                />
                                                            </Typography>
                                                            &thinsp;|&thinsp;
                                                            <Typography
                                                                fontSize={10}
                                                                fontWeight={500}
                                                                color="#44474E"
                                                                noWrap
                                                            >
                                                                {`${cluster.supplier_count} ${plural("supplier", cluster.supplier_count)}`}
                                                            </Typography>
                                                            &thinsp;|&thinsp;
                                                            <Typography
                                                                fontSize={10}
                                                                fontWeight={500}
                                                                color="#44474E"
                                                                noWrap
                                                            >
                                                                {`${cluster.transaction_count} ${plural("row", cluster.transaction_count)}`}
                                                            </Typography>
                                                        </Stack>

                                                        {authStore.isMithraStaffGroup && (
                                                            <FormControlLabel
                                                                control={
                                                                    cluster.busy ? (
                                                                        <CircularProgress
                                                                            size={42}
                                                                            className="p-2"
                                                                        />
                                                                    ) : (
                                                                        <Checkbox
                                                                            checked={cluster.limited_access_enabled}
                                                                            onChange={(_e, checked) =>
                                                                                filterController.setClusterLimitedAccess(
                                                                                    cluster.id,
                                                                                    checked,
                                                                                )
                                                                            }
                                                                        />
                                                                    )
                                                                }
                                                                label={"Enabled for limited access"}
                                                            />
                                                        )}
                                                    </>
                                                }
                                                className="goldern-record-cluster-li-text"
                                            />
                                        </ListItemButton>
                                    ))}
                                    {filterController.clusters_request_busy &&
                                        filterController.clusters.length === 0 &&
                                        Array.from(
                                            Array(
                                                Math.ceil(
                                                    (window.screen.height -
                                                        64 -
                                                        12 -
                                                        31 -
                                                        8 -
                                                        40 -
                                                        8 -
                                                        43) /
                                                    50,
                                                ),
                                            ),
                                        ).map((_, index) => <GoldenRecordClusterMenuSkeleton key={index}/>)}
                                    {!(
                                        authStore.isMithraStaffGroup ||
                                        bagStore.configStore.getConfigKey("MH_UNLIMITED_ACCESS")
                                    ) && (
                                        <Stack
                                            sx={{
                                                backgroundColor: "white",
                                                borderRadius: 1,
                                                overflow: "hidden",
                                                my: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: "#FAD2B2",
                                                    width: "100%",
                                                    height: "5px",
                                                }}
                                            ></Box>
                                            <Stack sx={{px: 1.5, py: 2}}>
                                                <Typography variant="body2" sx={{mb: 0.5}}>
                                                    See all {filterController.clusters_count_server}{" "}
                                                    groups
                                                </Typography>
                                                <Link
                                                    href="https://www.mithra-ai.com/pricing"
                                                    target="_blank"
                                                    sx={{
                                                        textDecoration: "none",
                                                        color: "#4088F8",
                                                    }}
                                                >
                                                    Upgrade to access them all
                                                </Link>
                                            </Stack>
                                        </Stack>
                                    )}
                                    {(authStore.isMithraStaffGroup ||
                                            bagStore.configStore.getConfigKey("MH_UNLIMITED_ACCESS")) &&
                                        filterController.clusters_count_server !== undefined &&
                                        filterController.clusters_count_server >
                                        filterController.clusters.length && (
                                            <GoldenRecordClusterMenuSkeleton/>
                                        )}
                                </List>
                            </>
                        )}
                    </>
                )}
            </Box>
        );
    });

function GoldenRecordClusterMenuSkeleton() {
    return (
        <ListItem sx={{px: 0}}>
            <CustomSkeleton width={232} height={50}/>
        </ListItem>
    );
}
